import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { DeviceViewSettingsTable } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { CONFIRM_CLOSE_DIALOG } from 'components/DeviceView/deviceViewConstants';
import type {
  DeviceViewEntity,
  SelectedTable,
  SelectedTables,
} from 'components/DeviceView/deviceViewTypes';
import { getCloudConstructLabelFromNode } from 'components/DeviceView/helpers/cloudConstruct';
import { getDeviceViewEntityTables } from 'components/DeviceView/helpers/getDeviceViewEntityTables';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { selectMenuTree } from 'components/GlobalState/selectors/selectMenuTree';
import { ModalWindow } from 'components/ModalWindow/ModalWindow';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import LeaveDialogue from 'containers/LeaveDialogue';
import type { SaveEvent } from 'containers/LeaveDialogue/LeaveDialog';
import { equals } from 'ramda';
import { type ChangeEvent, type FC, memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { apiTableTreeFromMenu } from '../helpers/apiTableTreeFromMenu';
import { getApiTablePaths } from '../helpers/getApiTablePaths';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import { updateDeviceViewSettings } from '../model/commands/updateDeviceViewSettings';
import { DeviceViewSetupTablesSearchResults } from './DeviceViewSetupTablesSearchResults';
import { DeviceViewSetupTablesSelected } from './DeviceViewSetupTablesSelected';
import { DeviceViewSetupTablesTree } from './DeviceViewSetupTablesTree';

interface Props {
  deviceViewEntity: DeviceViewEntity;
  onCloseSetupTables: () => void;
}

const BareDeviceViewSetupTables: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    deviceViewContainer: prop('deviceViewContainer'),
    routesWithApiEndpoint: prop('routesWithApiEndpoint'),
    settings: prop('settings'),
    pathsWithName: prop('pathsWithName'),
  });
  const globalStateCtxProps = useGlobalStateContextSelectors({
    menuTree: selectMenuTree,
    oas: prop('oas'),
  });
  const navigate = useNavigate();
  const msg = useLocale();
  const confirmDialog = useConfirmDialog({ name: CONFIRM_CLOSE_DIALOG });
  const [selelctedTablesPristine, setSelectedTablesPristine] =
    useState<boolean>(true);
  const paths = useMemo(
    () =>
      getApiTablePaths(globalStateCtxProps.oas, ctxProps.routesWithApiEndpoint),
    [ctxProps.routesWithApiEndpoint, globalStateCtxProps.oas],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: initial value
  const initialSelectedTables = useMemo(
    () =>
      getDeviceViewEntityTables(
        props.deviceViewEntity,
        ctxProps.settings,
        ctxProps.pathsWithName,
      ),
    [],
  );
  const [selectedTables, setSelectedTables] = useState<SelectedTables>(
    initialSelectedTables.map((table: DeviceViewSettingsTable) => ({
      label: table.label,
      // Fixed noUncheckedIndexedAccess NIM-19408
      path: paths[table.webEndpoint]!,
      webEndpoint: table.webEndpoint,
    })),
  );
  const [search, setSearch] = useState<string>('');

  const tree = apiTableTreeFromMenu(
    globalStateCtxProps.oas,
    search,
    props.deviceViewEntity.deviceViewElement,
  );

  // Per NIM-24243 AC3, the dialog title is "Set up tables for {construct}"
  const cloudConstructLabel =
    props.deviceViewEntity.deviceViewElement === 'node'
      ? getCloudConstructLabelFromNode(
          props.deviceViewEntity.nodeType,
          props.deviceViewEntity.extra,
        )
      : null;
  const dialogTitle =
    cloudConstructLabel !== null
      ? msg.deviceView.setupTables.title.replace(
          '{construct}',
          cloudConstructLabel,
        )
      : msg.deviceView.setupTables.title;

  const leaveDialogMessages = useMemo(
    () => ({
      header: msg.deviceView.setupTables.confirmDialog.header,
      subHeader: msg.deviceView.setupTables.confirmDialog.subHeader,
      btnSave: msg.deviceView.setupTables.confirmDialog.btnConfirm,
      btnLeave: msg.deviceView.setupTables.confirmDialog.btnSecondaryConfirm,
    }),
    [msg],
  );

  const saveTables = useStableCallback(async () => {
    return ctxProps.dispatch(updateDeviceViewSettings, {
      selectedTables: selectedTables.map((table: SelectedTable) => ({
        label: table.label,
        webEndpoint: table.webEndpoint,
      })),
      deviceViewEntity: props.deviceViewEntity,
    });
  });

  const handleSave = useStableCallback(async () => {
    await saveTables();

    props.onCloseSetupTables();
  });

  const handleSaveLeaveDialog = useStableCallback(async (event: SaveEvent) => {
    await saveTables();

    // redirect from LeaveDialog after saving settings
    navigate(event.pathname, { state: { canLeave: true } });
  });

  const handleSelectedTablesChange = useStableCallback(
    (data: SelectedTables) => {
      const updatedTables = data.map(
        ({ path, ...other }): DeviceViewSettingsTable => other,
      );
      setSelectedTablesPristine(equals(updatedTables, initialSelectedTables));
      setSelectedTables(data);
    },
  );

  const handleClose = useStableCallback(() => {
    if (!selelctedTablesPristine) {
      confirmDialog.toggleConfirmDialog();
    } else {
      props.onCloseSetupTables();
    }
  });

  const handleCloseConfirm = useStableCallback(() => {
    props.onCloseSetupTables();
  });

  const handleSearchChange = useStableCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
  );

  return (
    <ModalWindow
      confirmClose={selelctedTablesPristine}
      container={ctxProps.deviceViewContainer}
      contentClassName='window-no-move p-lgu-3'
      onClose={handleClose}
      title={dialogTitle}
      content={
        <>
          <ConfirmDialog
            id={CONFIRM_CLOSE_DIALOG}
            msg={msg.deviceView.setupTables.confirmDialog}
            onClickConfirm={handleSave}
            onClickSecondaryConfirm={handleCloseConfirm}
            btnCancelAccent='negative'
          />
          <LeaveDialogue
            isPristine={selelctedTablesPristine}
            isValid
            msg={leaveDialogMessages}
            onSave={handleSaveLeaveDialog}
            saving={ctxProps.settings.saving}
          />
          <div
            className={classNames(
              'device-view-setup-tables__selected-tables-wrapper',
              'flex',
              'flex-col',
              'overflow-hidden',
              'lg:w-1/2',
              'w-full',
              'lg:h-full',
              'lg:mr-2.5',
              'mb-2.5',
              'lg:mb-0',
            )}
          >
            <DeviceViewSetupTablesSelected
              onUpdateTables={handleSelectedTablesChange}
              selectedTables={selectedTables}
            />
          </div>
          <div
            className={classNames(
              'device-view-setup-tables__tree-wrapper',
              'overflow-hidden',
              'flex',
              'flex-col',
              'w-full',
              'lg:w-1/2',
              'lg:pl-2.5',
              'lg:h-full',
            )}
          >
            <p className='mb-lgu-2 mt-0 font-bold'>
              {msg.deviceView.setupTables.availableTables}
            </p>
            <input
              className='form-control device-view-setup-tables__search'
              onChange={handleSearchChange}
              placeholder={msg.deviceView.setupTables.searchPlaceholder}
              value={search}
            />
            <ScrollColumnView
              className={classNames(
                'mt-lgu-3',
                search && 'device-view-setup-tables__search-results',
              )}
            >
              {search ? (
                <DeviceViewSetupTablesSearchResults
                  data={tree}
                  onAddTable={handleSelectedTablesChange}
                  routesWithApiEndpoint={ctxProps.routesWithApiEndpoint}
                  selectedTables={selectedTables}
                />
              ) : (
                <DeviceViewSetupTablesTree
                  data={tree}
                  onAddTable={handleSelectedTablesChange}
                  routesWithApiEndpoint={ctxProps.routesWithApiEndpoint}
                  selectedTables={selectedTables}
                />
              )}
            </ScrollColumnView>
          </div>
        </>
      }
      footer={
        <div className='p-lgu-3'>
          <Button
            variant='primary'
            className='mr-2'
            type='button'
            onClick={handleSave}
            icon='fa-floppy-disk'
            loading={ctxProps.settings.saving}
          >
            {msg.deviceView.setupTables.btnSave}
          </Button>
          <Button
            variant='secondary'
            type='button'
            onClick={handleClose}
            disabled={ctxProps.settings.saving}
          >
            {msg.deviceView.setupTables.btnClose}
          </Button>
        </div>
      }
      footerClassName='device-view-setup-tables__footer'
    />
  );
};

export const DeviceViewSetupTables = memo(BareDeviceViewSetupTables);
